import { FC, useEffect, useState, useRef } from 'react';
import styled from 'styled-components';
import Tippy from '@tippyjs/react';
import { ReactComponent as InfoIcon } from '../../../assets/icons/information.svg';

interface TooltipProps {
    content: string;
    children: React.ReactNode;
}

interface TippyInstance {
    state: {
        isVisible: boolean;
    };
    popper: HTMLElement | null;
    reference: Element | null;
    hide: () => void;
}

const Tooltip: FC<TooltipProps> = ({ content, children }) => {
    const [placement, setPlacement] = useState<'right' | 'top' | 'bottom'>('right');
    const [trigger, setTrigger] = useState<'mouseenter focus' | 'click'>('mouseenter focus');
    const tippyInstanceRef = useRef<TippyInstance | null>(null);

    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth <= 768) {
                setPlacement('top');
                setTrigger('click');
            } else {
                setPlacement('right');
                setTrigger('mouseenter focus');
            }
        };

        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (trigger === 'click' && tippyInstanceRef.current) {
                const target = event.target as HTMLElement;
                const instance = tippyInstanceRef.current;
                
                if (!instance.state.isVisible) return;
                
                const popper = instance.popper;
                const reference = instance.reference;
                
                // Проверяем, что клик не по иконке и не по tooltip
                if (reference && reference.contains(target)) {
                    return; // Клик по иконке - обрабатывается Tippy через toggle
                }
                
                if (popper && !popper.contains(target)) {
                    // Используем setTimeout, чтобы дать Tippy обработать клик на иконке первым
                    setTimeout(() => {
                        if (instance.state.isVisible) {
                            instance.hide();
                        }
                    }, 0);
                }
            }
        };

        if (trigger === 'click') {
            document.addEventListener('click', handleClickOutside);
            return () => document.removeEventListener('click', handleClickOutside);
        }
    }, [trigger]);

    return (
        <StyledTooltip>
            {children}
            <Tippy
                content={
                    <StyledTooltipContent 
                        onClick={() => {
                            if (trigger === 'click') {
                                tippyInstanceRef.current?.hide();
                            }
                        }}
                    >
                        {content}
                    </StyledTooltipContent>
                }
                delay={100}
                placement={placement}
                offset={[0, 8]}
                trigger={trigger}
                touch={trigger === 'click'}
                hideOnClick={trigger === 'click' ? 'toggle' : false}
                interactive={false}
                onCreate={(instance) => {
                    tippyInstanceRef.current = instance;
                }}
            >
                <StyledInfoIcon />
            </Tippy>
        </StyledTooltip>
    );
};

const StyledTooltip = styled.div`
    display: inline-block !important;
    font-size: 15px;
    font-weight: 300;
    line-height: 130%;
    text-align: center;
    gap: 4px;
`;
const StyledTooltipContent = styled.div`
    padding: 10px;
    background: var(--color-bg-main);
    box-shadow: var(--shadow-main);
    font-size: 14px;
    font-weight: 300;
    line-height: 150%; /* 21px */
    letter-spacing: 0.28px;
    max-width: 273px;
`;
const StyledInfoIcon = styled(InfoIcon)`
    vertical-align: middle;
    padding-left: 4px;
    max-width: 18px;
    max-height: 18px;
    width: 18px !important;
    margin-bottom: 0 !important;
    cursor: pointer;
`;

export default Tooltip;
